import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { iconoirArrowRight, iconoirBuilding, iconoirWarningTriangle } from '@ng-icons/iconoir';
import { BtnComponent } from '../../../components/ui/buttons/btn/btn.component';
import { SupabaseService } from '../../../core/supabase.service';
import { AuthService } from '../../../core/auth/auth.service';
import { LegalConsentService } from '../../../core/legal/legal-consent.service';
import { authMascot } from '../auth-mascots';

/** `naming` : e-mail confirmé, l'organisation reste à nommer (MOKA-360). */
type CallbackState = 'loading' | 'naming' | 'provisioning' | 'error';

/** Longueur maximale du nom, alignée sur l'edge function provision-organisation. */
export const ORGANISATION_LABEL_MAX = 80;

@Component({
  selector: 'app-auth-callback',
  imports: [NgIconComponent, ReactiveFormsModule, BtnComponent],
  providers: [
    provideIcons({ iconoirWarningTriangle, iconoirBuilding, iconoirArrowRight })
  ],
  templateUrl: './auth-callback.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './auth-callback.component.scss'
})
export class AuthCallbackComponent implements OnInit {

  private readonly supabase = inject(SupabaseService);
  private readonly authService = inject(AuthService);
  private readonly legalConsentService = inject(LegalConsentService);
  private readonly router = inject(Router);

  public readonly state = signal<CallbackState>('loading');
  public readonly errorMessage = signal<string | null>(null);

  /** Koffi « réflexion » pendant la vérification et l'activation. */
  public readonly mascot = authMascot('reflexion');
  public readonly namingMascot = authMascot('guide');

  /** Nom de l'organisation, demandé après la confirmation de l'e-mail. */
  public readonly organisationCtrl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(ORGANISATION_LABEL_MAX)]
  });
  /** Erreur affichée sous le champ (nom pris, vide…). */
  public readonly organisationError = signal<string | null>(null);
  /** L'erreur se corrige en relançant (provisionnement reprenable) plutôt qu'en repartant de zéro. */
  public readonly canRetry = signal(false);

  async ngOnInit(): Promise<void> {
    // supabase-js detecte automatiquement les tokens dans l'URL (hash ou query params)
    // et etablit la session via onAuthStateChange. On attend la session.
    const { data: { session }, error: sessionError } = await this.supabase.client.auth.getSession();

    if (sessionError || !session) {
      // Parfois la session n'est pas encore prête, on attend l'événement SIGNED_IN
      await this.waitForSession();
      return;
    }

    await this.handleSession(session);
  }

  private async waitForSession(): Promise<void> {
    return new Promise((resolve) => {
      const { data: { subscription } } = this.supabase.client.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          subscription.unsubscribe();
          await this.handleSession(session);
          resolve();
        } else if (event === 'INITIAL_SESSION' && !session) {
          subscription.unsubscribe();
          this.state.set('error');
          this.errorMessage.set('Ce lien de confirmation n\'est plus valide : il a expiré ou a déjà servi. Connectez-vous, ou recommencez l\'inscription pour recevoir un nouveau lien.');
          resolve();
        }
      });

      // Timeout de sécurité : 10s
      setTimeout(() => {
        subscription.unsubscribe();
        if (this.state() === 'loading') {
          this.state.set('error');
          this.errorMessage.set('La vérification prend trop de temps. Rouvrez le lien reçu par email, ou réessayez depuis la page de connexion.');
        }
        resolve();
      }, 10000);
    });
  }

  private async handleSession(session: NonNullable<Awaited<ReturnType<typeof this.supabase.client.auth.getSession>>['data']['session']>): Promise<void> {
    const user = session.user;

    const { data: profile } = await this.supabase.client
      .from('profiles')
      .select('id, organisation_id')
      .eq('id', user.id)
      .maybeSingle();

    // Profil déjà rattaché : on repasse quand même par le provisionnement, qui termine un
    // passage interrompu (essai, données de départ) et répond sans rien recréer sinon.
    if (profile?.organisation_id) {
      await this.provision();
      return;
    }

    // Inscriptions d'avant MOKA-360 : le nom était saisi dans le formulaire.
    const legacyName = (user.user_metadata?.['organisation'] as string | undefined)?.trim();
    if (legacyName) {
      this.organisationCtrl.setValue(legacyName);
      await this.provision(legacyName);
      return;
    }

    this.state.set('naming');
  }

  /** Validation de l'étape « Nommez votre organisation ». */
  public async submitOrganisation(event?: Event): Promise<void> {
    // Formulaire sans [formGroup] : on bloque l'envoi natif (Entrée), qui rechargerait la page.
    event?.preventDefault();
    if (this.state() !== 'naming') return;
    this.organisationCtrl.markAsTouched();
    const label = this.organisationCtrl.value.replace(/\s+/g, ' ').trim();
    if (!label) {
      this.organisationError.set('Indiquez le nom de votre entreprise : il nommera votre espace.');
      return;
    }
    if (label.length > ORGANISATION_LABEL_MAX) {
      this.organisationError.set(`Le nom ne doit pas dépasser ${ORGANISATION_LABEL_MAX} caractères.`);
      return;
    }
    await this.provision(label);
  }

  /** Relance après une erreur : le provisionnement reprend où il s'était arrêté. */
  public async retry(): Promise<void> {
    const label = this.organisationCtrl.value.trim();
    await this.provision(label || undefined);
  }

  /**
   * Crée (ou termine) l'espace de la personne. Un nom déjà pris ou manquant ramène à l'étape
   * de nommage, rien n'ayant été créé ; les autres échecs proposent de réessayer.
   */
  private async provision(organisation?: string): Promise<void> {
    this.state.set('provisioning');
    this.organisationError.set(null);

    const { error: provisionError } = await this.supabase.client.functions.invoke(
      'provision-organisation',
      { body: organisation ? { organisation } : {} }
    );

    if (provisionError) {
      const { status, code } = await readFunctionError(provisionError);

      if (status === 409 || code === 'organisation_label_taken') {
        this.state.set('naming');
        this.organisationError.set('Ce nom est déjà utilisé par une autre organisation. Ajoutez par exemple votre ville ou votre activité.');
        return;
      }

      if (code === 'organisation_required') {
        this.state.set('naming');
        return;
      }

      this.state.set('error');
      this.canRetry.set(true);
      this.errorMessage.set('Votre espace n\'a pas pu être préparé. Réessayez dans un instant : rien n\'est perdu, la préparation reprendra là où elle s\'est arrêtée.');
      return;
    }

    // Profil désormais provisionné → charger le profil puis rediriger vers l'app
    await this.authService.reloadProfileAfterProvisioning();
    await this.persistSignupConsents();
    await this.router.navigate(['/'], { replaceUrl: true });
  }

  /**
   * Persiste le consentement du profil aux CGU / politique de confidentialité / mentions légales
   * à l'issue de l'inscription — la checkbox « conditions » du formulaire de sign-up
   * (`SignUpFlowComponent`) n'est elle-même jamais enregistrée en base, seule sa validation
   * cliente bloque la soumission. `/auth/callback` est le seul point de la route de sign-up où
   * une session authentifiée existe (RLS `legal_consents` exige `profile_id = auth.uid()`) ET où
   * le profil vient d'être (re)chargé.
   *
   * Fail-open : une version déjà consentie (23505) est gérée en amont par
   * `LegalConsentService.consent()` comme un succès idempotent, et toute autre erreur est
   * seulement journalée — elle ne doit jamais bloquer l'inscription. Le gate de consentement (P4)
   * rattrapera au premier accès si une insertion a échoué ici.
   */
  private async persistSignupConsents(): Promise<void> {
    const versions = Object.values(this.legalConsentService.currentVersions());
    for (const version of versions) {
      if (!version) continue;
      try {
        const { error } = await this.legalConsentService.consent(version.id, 'signup');
        if (error) {
          console.error('[AuthCallbackComponent] persistSignupConsents failed for version', version.id, error);
        }
      } catch (error) {
        console.error('[AuthCallbackComponent] persistSignupConsents unexpected error for version', version.id, error);
      }
    }
  }
}

/** Statut HTTP et code d'erreur stable renvoyés par une edge function en échec. */
async function readFunctionError(error: unknown): Promise<{ status: number; code: string | null }> {
  const context = (error as { context?: Response }).context;
  const status = typeof context?.status === 'number' ? context.status : 0;
  let code: string | null = null;
  try {
    const body = await context?.clone().json();
    code = typeof body?.error === 'string' ? body.error : null;
  } catch {
    // Corps absent ou non JSON : seul le statut compte.
  }
  return { status, code };
}
